// ShareScore: generates a canvas snapshot of the player's run and shares it
// via the Web Share API (Level 2 with files) when available, otherwise falls
// back to copying a link to the clipboard and downloading the image.
import React, { useState } from "react";
import { Share2, Check, Download } from "lucide-react";
import { toast } from "sonner";
import { audio } from "@/game/audio";

// Public marketing URL. window.location.origin returns capacitor://localhost
// inside the iOS WebView, which breaks share links.
const SHARE_URL = "https://crossbeau.app/";

async function buildShareCanvas({ score, hits, mode, streak }) {
  const W = 1080, H = 1080;
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const c = canvas.getContext("2d");

  // BG — neon gradient + grid
  const bg = c.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0, "#14082e");
  bg.addColorStop(1, "#0B0C10");
  c.fillStyle = bg; c.fillRect(0, 0, W, H);

  // pink glow
  const g1 = c.createRadialGradient(W * 0.25, H * 0.1, 0, W * 0.25, H * 0.1, W * 0.5);
  g1.addColorStop(0, "rgba(255,0,127,0.35)");
  g1.addColorStop(1, "rgba(255,0,127,0)");
  c.fillStyle = g1; c.fillRect(0, 0, W, H);
  const g2 = c.createRadialGradient(W * 0.8, H * 0.9, 0, W * 0.8, H * 0.9, W * 0.55);
  g2.addColorStop(0, "rgba(0,255,255,0.3)");
  g2.addColorStop(1, "rgba(0,255,255,0)");
  c.fillStyle = g2; c.fillRect(0, 0, W, H);

  // grid
  c.strokeStyle = "rgba(255,255,255,0.06)";
  c.lineWidth = 1;
  for (let x = 0; x < W; x += 70) { c.beginPath(); c.moveTo(x, 0); c.lineTo(x, H); c.stroke(); }
  for (let y = 0; y < H; y += 70) { c.beginPath(); c.moveTo(0, y); c.lineTo(W, y); c.stroke(); }

  // top label
  c.fillStyle = "#fff";
  c.font = "bold 48px 'Fredoka', sans-serif";
  c.textAlign = "center";
  c.shadowColor = "#00FFFF"; c.shadowBlur = 18;
  c.fillText("CROSSBEAU CHAOS", W / 2, 150);

  c.shadowBlur = 0;
  c.fillStyle = "rgba(255,255,255,0.7)";
  c.font = "600 28px 'Nunito', sans-serif";
  const modeLabel = mode === "classic" ? "CLASSIC RUN" :
    mode === "boss" ? "BOSS CHALLENGE" :
    mode === "tiny_fast" ? "TINY & FAST" :
    mode === "zigzag" ? "ZIGZAG MADNESS" :
    mode === "rain" ? "BONUS RAIN" : mode.toUpperCase();
  c.fillText(modeLabel, W / 2, 200);

  // big score
  c.shadowColor = "#FFD700"; c.shadowBlur = 40;
  c.fillStyle = "#FFD700";
  c.font = "900 260px 'Fredoka', sans-serif";
  c.fillText(String(score), W / 2, 520);

  c.shadowBlur = 0;
  c.fillStyle = "#fff";
  c.font = "800 40px 'Fredoka', sans-serif";
  c.fillText("COINS", W / 2, 580);

  // stats row
  const drawStat = (x, label, value, color) => {
    c.shadowBlur = 0;
    c.fillStyle = "rgba(255,255,255,0.08)";
    c.strokeStyle = color;
    c.lineWidth = 3;
    const w = 300, h = 170;
    const rx = x - w / 2, ry = 650;
    roundRect(c, rx, ry, w, h, 28); c.fill(); c.stroke();
    c.shadowColor = color; c.shadowBlur = 18;
    c.fillStyle = color; c.font = "900 80px 'Fredoka', sans-serif";
    c.fillText(String(value), x, ry + 95);
    c.shadowBlur = 0;
    c.fillStyle = "rgba(255,255,255,0.75)";
    c.font = "700 24px 'Nunito', sans-serif";
    c.fillText(label, x, ry + 140);
  };
  drawStat(W / 2 - 180, "HITS", hits, "#00FFFF");
  drawStat(W / 2 + 180, "STREAK", `${streak || 1}d`, "#FF007F");

  // tagline
  c.shadowBlur = 0;
  c.fillStyle = "rgba(255,255,255,0.85)";
  c.font = "bold 36px 'Nunito', sans-serif";
  c.fillText("Can you beat my score?", W / 2, 920);

  c.fillStyle = "rgba(255,255,255,0.55)";
  c.font = "600 26px 'Nunito', sans-serif";
  c.fillText("Play Crossbeau Chaos", W / 2, 970);


  return canvas;
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function canvasToBlob(canvas) {
  return new Promise((res) => canvas.toBlob(res, "image/png"));
}

export default function ShareScore({ score, hits, mode, streak }) {
  const [shared, setShared] = useState(false);
  const [busy, setBusy] = useState(false);

  async function handleShare() {
    if (busy) return;
    setBusy(true);
    audio.buy();
    try {
      const canvas = await buildShareCanvas({ score, hits, mode, streak });
      const blob = await canvasToBlob(canvas);
      const file = new File([blob], "crossbow-chaos-score.png", { type: "image/png" });
      const url = SHARE_URL;
      const shareText = `I scored ${score} coins in Crossbeau Chaos! Can you beat me? ${url}`;

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "Crossbeau Chaos",
          text: shareText,
        });
        setShared(true);
        toast.success("Shared! Go crush those bragging rights.");
      } else if (navigator.share) {
        await navigator.share({ title: "Crossbeau Chaos", text: shareText, url });
        setShared(true);
        toast.success("Shared!");
      } else {
        // fallback: download image + copy text
        const dataUrl = canvas.toDataURL("image/png");
        const a = document.createElement("a");
        a.href = dataUrl;
        a.download = "crossbow-chaos-score.png";
        document.body.appendChild(a); a.click(); a.remove();
        try { await navigator.clipboard.writeText(shareText); } catch {}
        setShared(true);
        toast.success("Image saved! Caption copied to clipboard.");
      }
    } catch (e) {
      if (e && e.name === "AbortError") {
        // user canceled — no toast
      } else {
        toast.error("Sharing failed. Try again?");
      }
    } finally {
      setBusy(false);
    }
  }

  const hasShare = typeof navigator !== "undefined" && !!navigator.share;

  return (
    <button
      onClick={handleShare}
      disabled={busy}
      data-testid="share-score-btn"
      className="btn-neon btn-yellow w-full disabled:opacity-60"
    >
      {shared ? (
        <><Check className="w-5 h-5" /> Shared!</>
      ) : hasShare ? (
        <><Share2 className="w-5 h-5" /> Brag Score</>
      ) : (
        <><Download className="w-5 h-5" /> Save & Brag</>
      )}
    </button>
  );
}
